import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { signOut } from '../auth/cognito';
import { clearStoredSessionId } from '../auth/sessionId';
import { api, sendOfflineBeacon } from '../api/client';
import { useDevice } from '../twilio/useDevice';
import { IncomingCallModal } from './IncomingCallModal';
import { CallControlsPane } from './CallControlsPane';
import { NotificationBell } from './NotificationBell';
import { useDeviceStore } from '../store/device';
import { displayName } from '../utils/display';

/**
 * Shared layout shell for all authenticated pages. Mounted once by
 * AuthedLayout (App.tsx) and kept alive across route changes — so the
 * Voice SDK Device, token-refresh timers, beforeunload listener, and
 * activeCall state all survive Dashboard ↔ History ↔ Users navigation.
 * That's what keeps a call from getting hung up when the agent moves
 * between pages.
 */
export function Layout({ children }: { children: ReactNode }) {
  const user = useAuth(s => s.user);
  const idToken = useAuth(s => s.idToken);
  const sessionRevoked = useAuth(s => s.sessionRevoked);
  const clear = useAuth(s => s.clear);
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);

  useDevice();

  const activeCall = useDeviceStore(s => s.activeCall);

  // FR-34: best-effort Offline beacon when the tab closes.
  useEffect(() => {
    if (!idToken) return;
    const onBeforeUnload = () => sendOfflineBeacon(idToken);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [idToken]);

  // FR-64: 30s liveness heartbeat while the tab is alive. The scheduled
  // PresenceDemoter Lambda demotes stale-Available agents (>5min idle to
  // Away, >10min to Offline) so dead browsers stop getting ringed by
  // TaskRouter. Fire-and-forget — failures don't surface to the user.
  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    function tick() {
      if (cancelled) return;
      api.heartbeat().catch(err => console.warn('heartbeat failed', err));
    }
    tick(); // fire immediately on mount + re-auth
    const id = window.setInterval(tick, 30_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [idToken]);

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await api.setAgentStatus('Offline');
    } catch (err) {
      console.error('failed to set Offline before sign-out', err);
    }
    try {
      // FR-66: release the active-session record so the next sign-in
      // (here or on another device) doesn't trigger a takeover prompt.
      await api.releaseSession();
    } catch (err) {
      console.error('failed to release session before sign-out', err);
    }
    clearStoredSessionId();
    signOut();
    clear();
  }

  // FR-66: another device took the session (or admin force-kicked).
  // Tear down the local state — the parent route guard will bounce
  // us to /login on the next render. We stash a one-shot flag in
  // sessionStorage so the login page can surface a reason banner.
  useEffect(() => {
    if (!sessionRevoked) return;
    try {
      window.sessionStorage.setItem('lumi:signedOutReason', 'kicked');
    } catch {
      // sessionStorage disabled — non-fatal; user will just bounce silently.
    }
    clearStoredSessionId();
    signOut();
    clear();
  }, [sessionRevoked, clear]);

  return (
    <div className="min-h-screen bg-slate-50">
      <IncomingCallModal />
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-semibold text-slate-900">Lumi Agent Console</Link>
            <nav className="flex items-center gap-4 text-sm">
              <NavLink to="/" current={location.pathname}>Dashboard</NavLink>
              <NavLink to="/history" current={location.pathname}>History</NavLink>
              {user?.role === 'admin' && (
                <NavLink to="/admin/users" current={location.pathname}>Users</NavLink>
              )}
            </nav>
          </div>
          {user && (
            <div className="flex items-center gap-3 text-sm">
              <NotificationBell />
              <span className="text-slate-700 font-medium" title={user.email}>{displayName(user)}</span>
              <span className="text-xs uppercase tracking-wide text-slate-400">{user.role}</span>
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="text-xs text-slate-500 hover:text-slate-900 disabled:opacity-50"
              >
                {signingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-4">
        {/* Call controls show on every authenticated page during an active
            call, so the agent can hang up, mute, or transfer without
            navigating back to Dashboard. */}
        {activeCall && <CallControlsPane />}
        {children}
      </main>
    </div>
  );
}

function NavLink({ to, current, children }: { to: string; current: string; children: ReactNode }) {
  const active = current === to || (to !== '/' && current.startsWith(to));
  return (
    <Link
      to={to}
      className={active ? 'text-brand-dark font-medium' : 'text-slate-600 hover:text-slate-900'}
    >
      {children}
    </Link>
  );
}
